import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  MaintenancePlanForm,
  MaintenancePlanFormValues,
} from "../../components/maintenance-plan-form";
import { Modal } from "../../components/modal";
import { useGetMaintenancePlan } from "../../queries/maintenance-plan";
import { updateMaintenancePlan } from "../../services/maintenance-plan";
import { recordIdToId } from "../../utils/record-id-to-id";
import { navigateBack } from "../../utils/navigate-back";

export function MaintenancePlanEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("Maintenance plan ID is required");
  }

  const { data: maintenancePlan, isLoading: isLoadingPlan } =
    useGetMaintenancePlan(id);
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [formValues, setFormValues] = useState<MaintenancePlanFormValues>({
    maintenancePlanCode: "",
    propertyPart: "",
    description: "",
    cost: "",
    yearlyFrequency: "",
  });

  useEffect(() => {
    if (maintenancePlan) {
      setFormValues({
        maintenancePlanCode: recordIdToId(
          maintenancePlan.maintenancePlanCode.id,
        ),
        propertyPart: recordIdToId(maintenancePlan.propertyPart.id),
        description: maintenancePlan.description,
        cost:
          maintenancePlan.cost !== undefined
            ? String(maintenancePlan.cost)
            : "",
        yearlyFrequency:
          maintenancePlan.yearlyFrequency !== undefined
            ? String(maintenancePlan.yearlyFrequency)
            : "",
      });
    }
  }, [maintenancePlan]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/maintenance/plan");
  };

  return (
    <Modal open onClose={closeModal}>
      <MaintenancePlanForm
        hasError={hasError}
        loading={isLoadingPlan || loading}
        values={formValues}
        onCancel={closeModal}
        onChange={setFormValues}
        onSubmit={() => {
          void (async () => {
            setLoading(true);
            setHasError(false);
            try {
              await updateMaintenancePlan(id, {
                maintenancePlanCode: formValues.maintenancePlanCode,
                propertyPart: formValues.propertyPart,
                description: formValues.description,
                cost:
                  formValues.cost !== ""
                    ? Number(formValues.cost)
                    : undefined,
                yearlyFrequency:
                  formValues.yearlyFrequency !== ""
                    ? Number(formValues.yearlyFrequency)
                    : undefined,
              });
              await Promise.all([
                queryClient.invalidateQueries({
                  queryKey: ["maintenancePlan", id],
                }),
                queryClient.invalidateQueries({
                  queryKey: ["maintenancePlans"],
                }),
              ]);
              closeModal();
            } catch {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
